<template>
  <div class="TaskPanel">
    <b-overlay :show="saving" rounded="sm">
      <section class="task-panel-section">
        <div class="details-header">
          <h4 class="details-title">Task Settings</h4>
          <div class="details-header-actions">
            <button type="button" class="btn btn-success btn-sm mr-2" @click="save" :disabled="saving" data-test="task-settings-save">
              <b-spinner v-if="saving" small class="mr-2"></b-spinner>
              <i v-else class="fa fa-save mr-2"></i>
              {{ saving ? 'Saving...' : 'Save' }}
            </button>
            <button type="button" class="btn btn-outline-secondary btn-sm" @click="closePanel" :disabled="saving">Cancel</button>
          </div>
        </div>

        <div class="card panel task-details-main">
          <div class="task-details-main-item">
            <label class="detail-label-block">Notifications</label>
            <b-form-checkbox v-model="soundPushEnabled" switch :disabled="saving" data-test="task-sound-push-toggle">
              <span class="switch-label">Play sound &amp; desktop notifications when I'm assigned a task</span>
            </b-form-checkbox>
          </div>

          <div v-if="soundPushEnabled" class="task-details-main-item">
            <NotificationSoundPicker
              v-model="notificationSound"
              label="Task notification sound"
              hint="Click ▶ to preview. This sound plays when someone assigns you a task."
            />
          </div>

          <div class="task-details-main-item">
            <NotificationHelp :testSoundFile="notificationSound" />
          </div>
        </div>

        <div class="card panel task-details-main">
          <div class="task-details-main-item">
            <label class="detail-label-block">Permissions</label>
            <p class="perm-prompt">Who can delete tasks?</p>
            <b-form-group class="abc-radio">
              <b-form-radio v-model="whoCanDelete" class="custom-radio" name="tasksWhoCanDelete" value="creator" :disabled="saving">
                <span class="switch-label">Only the creator of the task</span>
              </b-form-radio>
              <b-form-radio v-model="whoCanDelete" class="custom-radio mt-2" name="tasksWhoCanDelete" value="anyone" :disabled="saving">
                <span class="switch-label">Any employee of this practice</span>
              </b-form-radio>
            </b-form-group>
          </div>
        </div>
      </section>
    </b-overlay>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import NotificationSoundPicker from '@/components/NotificationSoundPicker.vue';
import NotificationHelp from '@/components/NotificationHelp.vue';
import { getPrefs, setPref } from '@/utils/notificationPrefs';

export default {
  name: 'TaskSettings',
  components: { NotificationSoundPicker, NotificationHelp },

  data() {
    return {
      soundPushEnabled: true,
      notificationSound: 'bell-123742.mp3',
      whoCanDelete: 'creator',
      saving: false,
    };
  },

  computed: {
    userID() {
      return this.$store.state.customer.userID;
    },
    siteID() {
      return this.$store.state.customer.siteID;
    },
    // Read whoCanDelete directly off the settings array — bypassing the
    // broken `state.customer.allSettings` path that other panels used to use.
    whoCanDeleteFromDB() {
      const arr = this.$store.state.customer.settings;
      if (!Array.isArray(arr)) return 'creator';
      const row = arr.find(s => s && s.Property === 'TASKS_WHO_CAN_DELETE');
      return (row && row.PropertyValue) || 'creator';
    },
  },

  created() {
    this.loadFromPrefs();
  },

  methods: {
    ...mapActions('customer', ['saveSettings', 'getSettings']),

    loadFromPrefs() {
      const prefs = getPrefs(this.userID);
      this.soundPushEnabled = prefs.task.soundPush;
      this.notificationSound = prefs.task.sound;
      this.whoCanDelete = this.whoCanDeleteFromDB;
    },

    async save() {
      this.saving = true;
      try {
        // Per-user notification prefs → localStorage (no network).
        setPref(this.userID, 'task', {
          soundPush: this.soundPushEnabled,
          sound: this.notificationSound,
        });

        // Site-wide policy → DB (only TASKS_WHO_CAN_DELETE; the legacy
        // TASKS_SOUND_PUSH_ENABLED + TASKS_NOTIFICATION_SOUND keys are
        // intentionally NOT written here — they're orphaned and not read
        // anywhere after this PR ships).
        const payload = {
          settingsFor: 'TASKS',
          settings: {
            TASKS_WHO_CAN_DELETE: this.whoCanDelete,
          },
          siteID: this.siteID,
        };
        this.$store.dispatch('alert/setErrored', false);
        await this.saveSettings(payload);
        if (this.$store.state.alert && this.$store.state.alert.errored) {
          this.$toast.error('Failed to save task settings. Please try again.');
          return;
        }
        await this.getSettings({ siteID: this.siteID });
        this.$toast.success('Task settings saved!');
        this.$emit('closePanel', {});
      } catch (e) {
        console.error('TaskSettings save failed:', e);
        this.$toast.error('Failed to save task settings. Please try again.');
      } finally {
        this.saving = false;
      }
    },

    closePanel() {
      this.$emit('closePanel', {});
    },
  },
};
</script>

<style lang="scss" scoped>
/* Mirrors TodoDetails.vue layout. */

.TaskPanel {
  font-family: 'Inter', 'Segoe UI', Roboto, -apple-system, BlinkMacSystemFont, 'Helvetica Neue', Arial, sans-serif;
  color: #334155;
  background-color: #f8fafc;
  height: 100%;
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

.task-panel-section {
  display: flex;
  flex-direction: column;
  padding: 1rem;
  flex: 1;
}

.details-header {
  margin-bottom: 0.875rem;
  border-bottom: 1px solid #e5e7eb;
  padding-bottom: 0.75rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}

.details-header-actions {
  display: flex;
  align-items: center;
}

.details-title {
  font-size: 1.15rem;
  font-weight: 600;
  color: #0f172a;
  margin: 0;
}

.task-details-main {
  background-color: white;
  border-radius: 0.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  border: 1px solid #e5e7eb;
  margin-bottom: 1rem;
  padding: 1rem;
}

.task-details-main-item {
  margin-bottom: 1rem;

  &:last-child {
    margin-bottom: 0;
  }
}

.detail-label-block {
  display: block;
  font-weight: 600;
  color: #475569;
  font-size: 0.875rem;
  margin-bottom: 0.5rem;
}

.switch-label {
  color: #0f172a;
  font-size: 0.9375rem;
}

.perm-prompt {
  margin: 0 0 0.5rem;
  color: #475569;
  font-size: 0.875rem;
}
</style>
